import { StandardEvents } from '@shopify/events';

class AniarcsJerseyBundles extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    const data = JSON.parse(this.querySelector('[data-bundle-data]').textContent);
    this.product = data.current;
    this.catalog = data.catalog.filter(product => product && product.available);
    this.labels = data.labels;
    this.form = this.closest('form');
    this.section = this.closest('.shopify-section');
    this.variant = this.product.variants.find(v => String(v.id) === this.form.elements.id.value);
    this.bundleCount = 1;
    this.slots = new Map();
    this.money = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: this.dataset.currency || 'GBP'
    });
    this.querySelectorAll('[data-bundle-count]').forEach(input => {
      input.addEventListener('change', () => {
        this.bundleCount = Number(input.value);
        this.sync();
      }, { signal });
    });
    this.section.addEventListener(StandardEvents.productSelect, async event => {
      if (event.target.closest('product-card')) return;
      this.variantPending = true;
      try {
        const { detail } = await event.promise;
        if (detail?.productId && String(detail.productId) !== String(this.product.id)) return;
        this.variant = this.product.variants.find(v => String(v.id) === String(detail?.resource?.id));
        requestAnimationFrame(() => this.sync());
      } catch {
        this.showError(this.labels.unavailable);
      } finally {
        this.variantPending = false;
      }
    }, { signal });
    this.sync();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
  }

  chosenVariant(state) {
    return state.product.variants.find(v => v.options.every((value, index) => value === state.options[index]));
  }

  ensureSlot(number) {
    if (this.slots.has(number)) return;
    const product = this.catalog.find(p => p.id === this.product.id) || this.catalog[0];
    const variant = product?.variants.find(v => v.available);
    if (variant) this.slots.set(number, { product, options: [...variant.options] });
  }

  renderSlot(number) {
    this.ensureSlot(number);
    const state = this.slots.get(number);
    if (!state) return;
    const root = this.querySelector('[data-bundle-slot="' + number + '"] [data-bundle-controls]');
    root.replaceChildren();
    const label = document.createElement('label');
    label.textContent = this.labels.product;
    const select = document.createElement('select');
    select.setAttribute('aria-label', this.labels.product + ' ' + number + ' product');
    this.catalog.forEach(product => {
      const option = new Option(product.title, String(product.id), false, product.id === state.product.id);
      select.append(option);
    });
    select.addEventListener('change', () => {
      const product = this.catalog.find(p => String(p.id) === select.value);
      const variant = product.variants.find(v => v.available);
      this.slots.set(number, { product, options: [...variant.options] });
      this.renderSlot(number);
      this.sync();
    });
    label.append(select);
    root.append(label);
    state.product.options.forEach((option, index) => {
      const name = typeof option === 'string' ? option : option.name;
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'arc-bundle-options';
      const legend = document.createElement('legend');
      legend.textContent = name;
      const buttons = document.createElement('div');
      buttons.className = 'arc-bundle-option-buttons';
      [...new Set(state.product.variants.map(v => v.options[index]))].forEach(value => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = value;
        button.setAttribute('aria-pressed', String(state.options[index] === value));
        const candidate = [...state.options];
        candidate[index] = value;
        const exact = state.product.variants.find(v => v.available && v.options.every((o, i) => o === candidate[i]));
        const fallback = state.product.variants.find(v => v.available && v.options[index] === value);
        button.disabled = !fallback;
        button.addEventListener('click', () => {
          state.options = [...(exact || fallback).options];
          this.renderSlot(number);
          this.sync();
        });
        buttons.append(button);
      });
      fieldset.append(legend, buttons);
      root.append(fieldset);
    });
  }

  total(count) {
    const percentage = count === 3 ? 15 : count === 2 ? 10 : 0;
    const discounted = price => price - Math.floor(price * percentage / 100);
    let total = discounted(this.variant?.price || 0);
    for (let number = 2; number <= count; number++) {
      total += discounted(this.chosenVariant(this.slots.get(number) || { product: this.product, options: this.variant?.options || [] })?.price || this.variant?.price || 0);
    }
    return total;
  }

  sync() {
    const multiple = this.bundleCount > 1;
    this.querySelector('[data-bundle-picker]').hidden = !multiple;
    for (let number = 2; number <= 3; number++) {
      const slot = this.querySelector('[data-bundle-slot="' + number + '"]');
      slot.hidden = number > this.bundleCount;
      if (!slot.hidden && !this.slots.has(number)) this.renderSlot(number);
    }
    for (let count = 1; count <= 3; count++) {
      const total = this.total(count);
      this.querySelector('[data-bundle-price="' + count + '"]').textContent = this.money.format(total / 100);
      this.querySelector('[data-bundle-each="' + count + '"]').textContent = count > 1
        ? this.money.format(total / count / 100) + ' ' + this.labels.each : '';
    }
    const price = this.section.querySelector('product-price[data-product-id="' + this.product.id + '"] .price bdi');
    if (price) price.textContent = this.money.format(this.total(this.bundleCount) / 100);
    const quantity = this.form.querySelector('quantity-selector-component');
    if (quantity) quantity.hidden = multiple;
    this.form.querySelectorAll('accelerated-checkout,.shopify-payment-button,[ref="acceleratedCheckoutButtonContainer"]').forEach(element => {
      element.hidden = multiple;
    });
    const sticky = this.section.querySelector('sticky-add-to-cart');
    if (sticky) sticky.hidden = multiple;
    this.showError('');
  }

  showError(message) {
    const error = this.querySelector('[data-bundle-error]');
    error.textContent = message;
    error.hidden = !message;
  }

  async addBundle(event) {
    if (this.bundleCount === 1) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.pending) return;
    if (this.variantPending || !this.variant?.available) {
      this.showError(this.labels.unavailable);
      return;
    }
    const items = [{ id: this.variant.id, quantity: 1 }];
    for (let number = 2; number <= this.bundleCount; number++) {
      const variant = this.chosenVariant(this.slots.get(number));
      if (!variant?.available) {
        this.showError(this.labels.unavailable);
        return;
      }
      items.push({ id: variant.id, quantity: 1 });
    }
    this.pending = true;
    const button = this.form.querySelector('[type="submit"]');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(this.dataset.addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || this.labels.error);
      document.dispatchEvent(new CustomEvent('aniarcs:bundle-added', { detail: { items } }));
      window.location.assign(this.dataset.cartUrl);
    } catch (error) {
      this.showError(error.message || this.labels.error);
      this.pending = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('aniarcs-jersey-bundles')) {
  customElements.define('aniarcs-jersey-bundles', AniarcsJerseyBundles);
}

